import { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Zap, ArrowLeft, ImagePlus, Sparkles, Loader2, Youtube, Lock, Crop, Check, CheckCircle2, Clock, Play, Music, Plus, X, Image as ImageIcon, Camera, GripVertical, ChevronUp, ChevronDown, Star, Facebook, Instagram, Download, Copy, Film, Video, Scissors, Volume2, VolumeX } from "lucide-react";
import VideoTrimModal from "@/components/VideoTrimModal";
import CameraClipModal from "@/components/CameraClipModal";
import { videoPoster, placeholderPoster, uploadStepClip, STEP_CLIP_MAX_MB } from "@/lib/stepClip";
import PortraitCropModal from "@/components/PortraitCropModal";
import MinutePreviewModal from "@/components/MinutePreviewModal";
import MinuteVoiceStudio from "@/components/MinuteVoiceStudio";
import { ThumbnailComposer } from "@/components/ThumbnailComposer";
import CameraCaptureModal from "@/components/CameraCaptureModal";
import MinuteEndcardEditor from "@/components/MinuteEndcardEditor";
import SlideFilteredImage, { FILTER_OPTIONS, STRENGTH_OPTIONS } from "@/components/SlideFilteredImage";
import CreatorModeCards from "@/components/CreatorModeCards";
import { saveOrShareVideo } from "@/lib/saveVideo";
import { playBell } from "@/lib/bell";
import DiyStepRecorder from "@/components/DiyStepRecorder";
import { stitchClipTakes } from "@/lib/stitchVo";

const LAYOUTS = {
  "6x10": { label: "6 × 10s", desc: "6 Video Clips — up to 10 seconds each (fill only what you need)", durations: Array(6).fill(10) },
  "12x5": { label: "12 × 5s", desc: "12 Image Clips — 5 seconds each (fill only what you need)", durations: Array(12).fill(5) },
  combo: { label: "Combo", desc: "Mix — 3 Video Clips up to 10 seconds + 6 Image Clips at 5 seconds", durations: [5, 5, 5, 5, 5, 5, 10, 10, 10] },
};

const emptySlide = () => ({ headline: "", subtext: "", image: null, image_id: null, origSrc: null, cropped: false, prompt: "", busy: false, text_pos: "middle", text_size: "medium", filter: "none", filter_strength: "standard", motion: "none", kind: "video" });

// Stealth black panel with subtle green pinstripes — the Vittyyo "Black Render" look.
const PANEL = {
  background: "#0f0f16",
  backgroundImage: "repeating-linear-gradient(45deg, rgba(93,138,82,.055) 0px, rgba(93,138,82,.055) 10px, transparent 10px, transparent 20px)",
  border: "2px solid #5d8a52",
};

export default function MinuteMode() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { user } = useAuth();
  const isEdit = !!editId;
  const isAdmin = !!user?.unlimited;
  const balanceUsd = (user?.credits ?? 0) / 100;
  const SOCIAL_PRICE = 1.99;
  const lowBalance = !isEdit && !isAdmin && balanceUsd < SOCIAL_PRICE;
  const [loadingReel, setLoadingReel] = useState(!!editId);
  const [title, setTitle] = useState("");
  const [layout, setLayout] = useState("6x10");
  const [hideCounter, setHideCounter] = useState(false);
  const [isDesk, setIsDesk] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const on = (e) => setIsDesk(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [slides, setSlides] = useState(() => LAYOUTS["6x10"].durations.map(emptySlide));
  const [masterIdx, setMasterIdx] = useState(null);   // "Consistent Character": which slide is the master likeness
  const [lockWardrobe, setLockWardrobe] = useState(false);  // also keep the same outfit across generated slides
  const [creating, setCreating] = useState(false);
  const [pct, setPct] = useState(0);
  const [elapsed, setElapsed] = useState(0);          // live stopwatch (seconds) while rendering
  const [avgSeconds, setAvgSeconds] = useState(0);    // typical render time, to reassure the user
  const [renderReady, setRenderReady] = useState(null); // manual id once done — we STAY on this page
  const [renderState, setRenderState] = useState(null);   // {status, progress} while editing an in-flight reel
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [voDataUrl, setVoDataUrl] = useState(null);
  const [previewVo, setPreviewVo] = useState(null);
  const [voScript, setVoScript] = useState("");
  const [tracks, setTracks] = useState([]);
  const [musicMax, setMusicMax] = useState(6);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [musicDuck, setMusicDuck] = useState(0.15);
  const [motionIntensity, setMotionIntensity] = useState("subtle");
  const [quality, setQuality] = useState("hd");   // hd (1080p) | fast (720p, quicker render)
  const [musicSwell, setMusicSwell] = useState(true);
  const [ytDraft, setYtDraft] = useState({});
  const [musicBusy, setMusicBusy] = useState(false);
  const [thumbId, setThumbId] = useState(null);
  const [thumbPhotos, setThumbPhotos] = useState([]);
  const [thumbOpen, setThumbOpen] = useState(false);
  const [thumbResolving, setThumbResolving] = useState(false);
  const songRefs = useRef([]);
  const fileRefs = useRef([]);
  const [cropIndex, setCropIndex] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [camIndex, setCamIndex] = useState(null);
  const videoRefs = useRef([]);
  const [trimIdx, setTrimIdx] = useState(null);
  const [vidCamIdx, setVidCamIdx] = useState(null);
  const onPickVideo = async (i, file, knownSecs = 0) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    if (file.size > STEP_CLIP_MAX_MB * 1048576) { toast.error(`This clip is ${(file.size / 1048576).toFixed(1)}MB — the limit is ${STEP_CLIP_MAX_MB}MB`, { description: "Shoot at 720p or 1080p · 30fps (not 4K)." }); return; }
    try {
      const { poster, duration: probed } = await videoPoster(file);
      const duration = probed || knownSecs;
      if (duration > 20.5) { toast.error(`This clip is ${Math.round(duration)}s — clips can be up to 20 seconds`); return; }
      patch(i, { image: poster, image_id: null, clipFile: file, clipDur: duration, serverPoster: false, trimStart: 0, motion: "none", keepSound: !slides[i]?.vo });
      if (duration > 10.5) setTrimIdx(i);
    } catch {
      patch(i, { image: placeholderPoster(), image_id: null, clipFile: file, clipDur: knownSecs, serverPoster: true, trimStart: 0, motion: "none", keepSound: !slides[i]?.vo });
      toast.message("Clip added — its preview appears after upload");
    }
  };
  const [endcard, setEndcard] = useState({
    enabled: false, line1: "", line2: "", line3: "", font_size: "medium",
    text_color: "#ffffff", bg_color: "#1a2818", domain: "", logo_scale: 1,
    logo_image: null, logo_image_id: null, qr_image: null, qr_image_id: null,
    show_branding: true,
  });

  // Auto-brand from site: picker for scraped photos + one-tap end-card/cover fill.
  const [brandPhotos, setBrandPhotos] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedPhotos, setPickedPhotos] = useState({});

  const handleBrandPack = (data) => {
    if (!data) return;
    if (!title.trim() && data.business) setTitle(data.business);
    setEndcard((prev) => ({
      ...prev,
      enabled: true,
      line1: data.business || prev.line1,
      line2: data.domain || prev.line2,
      bg_color: data.bg_color || prev.bg_color,
      text_color: data.text_color || prev.text_color,
      domain: data.domain || prev.domain,
      logo_image: null,
      logo_image_id: data.logo_image_id || prev.logo_image_id,
      show_branding: true,
    }));
    if (data.headline) {
      setSlides((prev) => {
        const idx = prev.findIndex((s) => !s.headline && !s.image && !s.image_id);
        return idx === -1 ? prev : prev.map((s, i) => (i === idx ? { ...s, headline: data.headline } : s));
      });
    }
    if (Array.isArray(data.photos) && data.photos.length) {
      setBrandPhotos(data.photos);
      setPickedPhotos({});
      setPickerOpen(true);
    } else {
      toast.message("Couldn't grab usable photos from the site — add your own pictures below.");
    }
  };

  const applyPickedPhotos = () => {
    const chosen = brandPhotos.filter((p) => pickedPhotos[p.image_id]);
    if (!chosen.length) { setPickerOpen(false); return; }
    let placed = 0;
    setSlides((prev) => {
      const next = [...prev];
      let ci = 0;
      for (let i = 0; i < next.length && ci < chosen.length; i++) {
        if (!next[i].image && !next[i].image_id) {
          const p = chosen[ci++];
          next[i] = { ...next[i], image_id: p.image_id, origSrc: imageUrl(p.image_id), image: null, cropped: false };
          placed++;
        }
      }
      return next;
    });
    setPickerOpen(false);
    toast.success(placed ? `Added ${placed} site photo${placed > 1 ? "s" : ""} to your reel ✓` : "No empty picture slots left — clear a slide to add more");
  };

  const openCrop = (i) => {
    const s = slides[i];
    const src = s.origSrc || s.image || (s.image_id ? imageUrl(s.image_id) : null);
    if (!src) return;
    setCropIndex(i);
    setCropSrc(src);
  };
  const closeCrop = () => { setCropIndex(null); setCropSrc(null); };

  const loadMusic = async () => {
    try { const { data } = await api.get("/me/music"); setTracks(data.tracks || []); setMusicMax(data.max || 6); } catch { /* ignore */ }
  };
  useEffect(() => { loadMusic(); }, []);

  // Edit mode: load the existing reel and prefill the builder.
  useEffect(() => {
    if (!editId) return;
    let off = false;
    (async () => {
      try {
        const { data: m } = await api.get(`/manuals/${editId}`);
        if (off) return;
        if (m.mode !== "minute") { toast.error("That video isn't a Minute reel."); navigate("/my-videos"); return; }
        const steps = m.steps || [];
        const durs = steps.map((s) => (s.duration === 10 ? 10 : 5));
        let key = "12x5";
        if (durs.length === 6 && durs.every((d) => d === 10)) key = "6x10";
        else if (durs.length === 9) key = "combo";
        else if (durs.length === 12 && durs.every((d) => d === 5)) key = "12x5";
        setLayout(key);
        setTitle(m.title || "");
        setHideCounter(!!m.hide_counter);
        if (m.music_duck != null) setMusicDuck(m.music_duck);
        if (m.music_swell != null) setMusicSwell(!!m.music_swell);
        if (m.endcard) {
          setEndcard((prev) => ({
            ...prev,
            enabled: !!m.endcard.enabled,
            line1: m.endcard.line1 || "",
            line2: m.endcard.line2 || "",
            line3: m.endcard.line3 || "",
            font_size: m.endcard.font_size || "medium",
            logo_scale: m.endcard.logo_scale || 1,
            text_color: m.endcard.text_color || "#ffffff",
            bg_color: m.endcard.bg_color || "#1a2818",
            domain: m.endcard.domain || "",
            logo_image: null, logo_image_id: m.endcard.logo_image_id || null,
            qr_image: null, qr_image_id: m.endcard.qr_image_id || null,
            show_branding: m.endcard.show_branding !== false,
          }));
        }
        setMotionIntensity(m.motion_intensity || "subtle");
        setQuality(m.quality === "fast" ? "fast" : "hd");
        setVoScript(m.vo_script || "");
        setSlides(steps.map((s) => ({
          headline: s.headline || s.title || "",
          subtext: s.subtext || "",
          image: null,
          image_id: s.thumbnail_id || null,
          origSrc: s.thumbnail_id ? imageUrl(s.thumbnail_id) : null,
          cropped: false, prompt: "", busy: false,
          text_pos: s.text_pos || "middle", text_size: s.text_size || "medium", filter: s.filter || "none", filter_strength: s.filter_strength || "standard", motion: s.motion || "none",
        })));
        setMasterIdx(typeof m.master_slide === "number" && m.master_slide >= 0 && m.master_slide < steps.length ? m.master_slide : null);
        setLockWardrobe(!!m.lock_wardrobe);
      } catch {
        toast.error("Couldn't load that reel.");
        navigate("/my-videos");
      } finally { if (!off) setLoadingReel(false); }
    })();
    return () => { off = true; };
  }, [editId]); // eslint-disable-line

  // While editing an existing reel, watch its render status so we can offer a live "Cancel Render".
  useEffect(() => {
    if (!editId) return;
    let off = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/manuals/${editId}/status`);
        if (!off) setRenderState({ status: data.status, progress: data.progress });
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { off = true; clearInterval(t); };
  }, [editId]);

  const activeRender = ["rendering", "queued", "generating", "cancelling"].includes(renderState?.status);
  const cancelThisRender = async () => {
    if (cancelBusy) return;
    setCancelBusy(true);
    try {
      await api.post(`/manuals/${editId}/render/cancel`);
      toast.message("Cancelling render — your credit will be refunded.");
      setRenderState((s) => ({ ...(s || {}), status: "cancelling" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel this render.");
    } finally { setCancelBusy(false); }
  };

  const onUploadSong = (slot, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/audio\/(mpeg|mp3)/.test(f.type) && !f.name.toLowerCase().endsWith(".mp3")) { toast.error("Please choose an MP3 file"); return; }
    if (f.size > 12 * 1024 * 1024) { toast.error("Please upload an MP3 under 12 MB"); return; }
    const r = new FileReader();
    r.onload = async () => {
      setMusicBusy(true);
      try {
        const name = f.name.replace(/\.[^.]+$/, "").slice(0, 60) || "My Song";
        const { data } = await api.post("/me/music", { name, data: r.result, youtube_url: (ytDraft[slot] || "").trim() });
        setYtDraft((d) => ({ ...d, [slot]: "" }));
        await loadMusic();
        setSelectedTrackId(data.id);
        toast.success("Song saved 🎵");
      } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save that song"); }
      finally { setMusicBusy(false); }
    };
    r.readAsDataURL(f);
  };

  const deleteSong = async (tid) => {
    try { await api.delete(`/me/music/${tid}`); if (selectedTrackId === tid) setSelectedTrackId(null); await loadMusic(); toast.message("Song removed"); }
    catch { toast.error("Couldn't remove that song"); }
  };

  // Resolve chosen slide photos into server image_ids so the portrait Thumbnail Creator can use them.
  const openThumb = async () => {
    const withImg = slides.filter((s) => s.image || s.image_id);
    if (!withImg.length) { toast.error("Add at least one picture first"); return; }
    setThumbResolving(true);
    try {
      const ids = [];
      for (const s of withImg) {
        if (s.image_id) { ids.push(s.image_id); continue; }
        const { data } = await api.post("/thumbnails/upload", { image: s.image });
        if (data.image_id) ids.push(data.image_id);
      }
      setThumbPhotos(ids.map((id) => ({ id })));
      setThumbOpen(true);
    } catch { toast.error("Couldn't prepare your photos — try again"); }
    finally { setThumbResolving(false); }
  };

  const durations = LAYOUTS[layout].durations;
  const isFilled = (s) => !!(s.image || s.image_id);
  const filledEntries = slides.map((s, i) => ({ s, i, dur: durations[i] || 5 })).filter((x) => isFilled(x.s));
  const total = filledEntries.reduce((a, x) => a + x.dur, 0);
  const filledCount = filledEntries.length;
  // Per-clip voice takes (10s clips only), positioned at each clip's start on the reel timeline.
  const clipTakes = filledEntries.reduce((acc, x) => {
    if (x.dur === 10 && x.s.vo) acc.list.push({ url: x.s.vo, start: acc.t, maxDur: x.dur });
    acc.t += x.dur;
    return acc;
  }, { t: 0, list: [] }).list;
  const hasClipVo = slides.some((s, i) => s.vo && durations[i] === 10);
  const resolveVo = async () => (clipTakes.length ? stitchClipTakes(clipTakes) : voDataUrl);
  const openPreview = async () => {
    if (!slides.some((s) => s.image || s.image_id)) { toast.error("Add at least one picture to preview"); return; }
    try { setPreviewVo(await resolveVo()); } catch { setPreviewVo(voDataUrl); }
    setShowPreview(true);
  };

  const pickLayout = (key) => {
    setLayout(key);
    setSlides((prev) => LAYOUTS[key].durations.map((_, i) => prev[i] || emptySlide()));
  };

  const patch = (i, upd) => setSlides((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...upd } : s)));
  const dragFrom = useRef(null);
  const moveSlide = (from, to) => {
    if (from == null || to < 0 || to >= slides.length || from === to) return;
    setSlides((prev) => { const a = [...prev]; const [it] = a.splice(from, 1); a.splice(to, 0, it); return a; });
    // keep the master pointer on the same slide after reordering
    setMasterIdx((m) => {
      if (m === null) return m;
      if (m === from) return to;
      if (from < m && to >= m) return m - 1;
      if (from > m && to <= m) return m + 1;
      return m;
    });
  };

  // "Consistent Character": promote a slide to master (same person on every generated slide) or turn it off.
  const toggleMaster = (i) => {
    if (masterIdx === i) { setMasterIdx(null); toast.message("Consistent Character off"); return; }
    setMasterIdx(i);
    if (!slides[i].image_id && !slides[i].image) {
      toast.message(`Slide ${i + 1} is the master — generate or upload its image, and other slides will match that character`);
    } else {
      toast.success(`Slide ${i + 1} set as the master character ✨`);
    }
  };

  const onUpload = (i, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    const r = new FileReader();
    r.onload = () => patch(i, { image: r.result, image_id: null, origSrc: r.result, cropped: false });
    r.readAsDataURL(f);
  };

  const onGenerate = async (i) => {
    const prompt = (slides[i].prompt || "").trim();
    if (!prompt) { toast.error("Describe the picture first"); return; }
    patch(i, { busy: true });
    try {
      const body = { prompt };
      const masterImg = masterIdx !== null ? slides[masterIdx]?.image_id : null;
      const useRef = masterIdx !== null && i !== masterIdx && !!masterImg;
      if (useRef) { body.reference_image_id = masterImg; if (lockWardrobe) body.lock_wardrobe = true; }
      const { data } = await api.post("/manuals/minute/slide-image", body);
      patch(i, { image_id: data.image_id, image: null, origSrc: imageUrl(data.image_id), cropped: false, busy: false });
      toast.success(useRef ? `Slide ${i + 1} ready — same character ✨` : `Slide ${i + 1} image ready`);
    } catch (err) {
      patch(i, { busy: false });
      toast.error(err?.response?.data?.detail || "Couldn't generate that image");
    }
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  // Typical render time (reassurance) — refreshed on mount.
  useEffect(() => { api.get("/minute/render-eta").then(({ data }) => setAvgSeconds(data?.avg_seconds || 0)).catch(() => {}); }, []);
  // Live stopwatch while a render is running.
  useEffect(() => {
    if (!creating) return;
    setElapsed(0);
    const t0 = Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [creating]);

  // Smooth "faux" crawl so the bar never looks frozen while the server does heavy work.
  // Adaptive: targets the app's real typical render time (avgSeconds), clamped to 60–600s,
  // falling back to 300s until we know one. Always yields to real backend progress (never
  // goes backwards) and the poll loop snaps it to 100% the moment the render truly finishes.
  useEffect(() => {
    if (!creating) return;
    const target = Math.min(600, Math.max(60, avgSeconds > 0 ? avgSeconds : 300));
    const t0 = Date.now();
    const iv = setInterval(() => {
      const t = (Date.now() - t0) / 1000;                   // seconds since render kicked off
      const floor = Math.min(95, 12 + (t / target) * 83);    // 12% → 95% over the target time
      setPct((p) => (p >= 100 ? p : Math.max(p, floor)));
    }, 300);
    return () => clearInterval(iv);
  }, [creating, avgSeconds]);

  const createMinute = async () => {
    if (!title.trim()) { toast.error("Give your Minute a title"); return; }
    if (!slides.some((s) => s.image || s.image_id)) { toast.error("Add at least one picture"); return; }
    setCreating(true);
    setPct(4);
    setRenderReady(null);
    try {
      const payload = {
        title: title.trim(),
        platform: "vertical",
        hide_counter: hideCounter,
        music_duck: musicDuck,
        music_swell: musicSwell,
        motion_intensity: motionIntensity,
        quality,
        vo_script: voScript,
        master_slide: masterIdx !== null ? (filledEntries.findIndex((x) => x.i === masterIdx) >= 0 ? filledEntries.findIndex((x) => x.i === masterIdx) : null) : null,
        lock_wardrobe: lockWardrobe,
        endcard: {
          enabled: endcard.enabled,
          line1: endcard.line1, line2: endcard.line2, line3: endcard.line3,
          font_size: endcard.font_size,
          logo_scale: endcard.logo_scale,
          text_color: endcard.text_color, bg_color: endcard.bg_color,
          domain: endcard.domain,
          show_branding: endcard.show_branding,
          ...(endcard.logo_image ? { logo_image: endcard.logo_image } : { logo_image_id: endcard.logo_image_id }),
          ...(endcard.qr_image ? { qr_image: endcard.qr_image } : { qr_image_id: endcard.qr_image_id }),
        },
        slides: filledEntries.map(({ s, dur }) => ({
          duration: dur,
          headline: s.headline,
          subtext: s.subtext,
          text_pos: s.text_pos || "middle",
          text_size: s.text_size || "medium",
          filter: s.filter || "none",
          filter_strength: s.filter_strength || "standard",
          motion: s.motion || "none",
          ...(s.image_id ? { image_id: s.image_id } : s.image ? { image: s.image } : {}),
        })),
      };
      const { data } = isEdit
        ? await api.put(`/manuals/${editId}/minute`, payload)
        : await api.post("/manuals/minute", payload);
      const id = data.id;
      setPct(8);
      if (selectedTrackId) { try { await api.post(`/manuals/${id}/minute/music`, { track_id: selectedTrackId }); } catch { /* default bed */ } }
      let finalVo = voDataUrl;
      try { finalVo = await resolveVo(); } catch { toast.error("Couldn't combine your clip voice-overs"); }
      if (finalVo) { try { await api.post(`/manuals/${id}/minute/voiceover`, { audio: finalVo }); } catch { /* no VO */ } }
      if (thumbId) { try { await api.post(`/manuals/${id}/set-thumbnail`, { image_id: thumbId }); } catch { /* keep auto cover */ } }
      // Social clips: upload each 10s video slide (2 at a time) before the render starts.
      const clipJobs = filledEntries.map((x, k) => ({ ...x, k })).filter(({ s: sl, dur }) => dur === 10 && sl.kind === "video" && sl.clipFile);
      if (clipJobs.length) {
        let done = 0;
        const q = [...clipJobs];
        const worker = async () => {
          while (q.length) {
            const { s: sl, k } = q.shift();
            try { await uploadStepClip(id, k, sl.clipFile, sl.keepSound, null, sl.serverPoster, sl.trimStart); }
            catch (e) { toast.error(e?.response?.data?.detail || `Clip ${k + 1} didn't upload — its first frame is used as a photo`); }
            done += 1; setPct(8 + Math.round((done / clipJobs.length) * 4));
          }
        };
        await Promise.all([worker(), worker()]);
      }
      setPct(12);
      await api.post(`/manuals/${id}/minute/render`);
      // Poll status — surface the live percentage. We DO NOT navigate away; the user stays here.
      let done = false;
      for (let i = 0; i < 300 && !done; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: st } = await api.get(`/manuals/${id}/status`);
          if (typeof st.progress === "number") setPct((p) => Math.max(p, Math.min(99, st.progress)));
          if (st.status === "ready") { setPct(100); toast.success("Your reel is ready! 🎬"); playBell(); setRenderReady(id); done = true; }
          else if (st.status === "error") { toast.error(st.error || "Render failed"); done = true; }
        } catch { /* keep polling */ }
      }
      if (!done) toast.message("Still rendering — it'll appear on your Dashboard the moment it's done.", { duration: 6000 });
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err?.response?.data?.detail || "Not enough balance — add funds to create your reel.");
        navigate("/pricing");
        return;
      }
      toast.error(err?.response?.data?.detail || "Couldn't create your Minute");
    } finally {
      setCreating(false);
    }
  };

  // --- Share the finished reel (Social Mode only) ---
  const [sharingIg, setSharingIg] = useState(false);
  const shareToFacebook = () => {
    const id = renderReady;
    if (!id) return;
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    const shareUrl = `${base}/api/share/${id}`;
    const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(fb, "_blank", "noopener,noreferrer,width=680,height=720");
    toast.message("Opening Facebook — post the link to your feed. The reel preview comes from your public Vittyyo page.");
  };
  const saveForInstagram = async () => {
    const id = renderReady;
    if (!id || sharingIg) return;
    setSharingIg(true);
    toast.loading("Saving your reel for Instagram…", { id: "ig-dl" });
    try {
      const res = await api.get(`/manuals/${id}/video/download`, { responseType: "blob" });
      const filename = `stepmystep-reel-${id.slice(0, 8)}.mp4`;
      await saveOrShareVideo(res.data, filename, "Vittyyo reel");
      toast.success("Saved! Open Instagram on your phone and post it as a Reel 📲", { id: "ig-dl", duration: 7000 });
    } catch (e) {
      toast.error("Couldn't save the reel — please try again", { id: "ig-dl" });
    } finally {
      setSharingIg(false);
    }
  };
  const [captionBusy, setCaptionBusy] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captionText, setCaptionText] = useState("");
  const loadCaption = async (regenerate) => {
    const id = renderReady;
    if (!id || captionBusy) return;
    setCaptionBusy(true);
    toast.loading(regenerate ? "Rewriting your caption…" : "Writing your caption…", { id: "cap" });
    try {
      const { data } = await api.post(`/manuals/${id}/social-caption`, regenerate ? { regenerate: true } : {});
      setCaptionText(data?.text || "");
      setCaptionOpen(true);
      toast.success(regenerate ? "Fresh caption ready — tweak it if you like ✨" : "Caption ready — tweak it, then copy 📋", { id: "cap" });
    } catch (e) {
      toast.error("Couldn't write a caption — please try again", { id: "cap" });
    } finally {
      setCaptionBusy(false);
    }
  };
  const copyCaptionText = async () => {
    const text = (captionText || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); ta.remove();
    }
    toast.success("Caption + hashtags copied — paste it when you post! 📋", { duration: 5000 });
  };

  return (
    <div className="vdark max-w-3xl mx-auto px-4 py-6" data-testid="minute-mode-page">
      <button onClick={() => navigate(isEdit ? "/my-videos" : "/studio")} className="inline-flex items-center gap-1.5 text-sm font-bold text-white/70 hover:text-white mb-4" data-testid="minute-back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      {loadingReel ? (
        <div className="flex items-center justify-center py-24 text-white/60" data-testid="minute-edit-loading"><Loader2 size={22} className="spin mr-2" /> Loading your reel…</div>
      ) : (
      <>

      {!isEdit && <div className="-mb-3"><CreatorModeCards active="social" /></div>}

      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className={isEdit ? "flex items-center gap-2" : "sr-only"}>
          <Zap size={22} className="text-[#ffd21e]" />
          <h1 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Reel" : "Social Video Mode"}</h1>
        </div>
        <div className="flex items-center gap-2" data-testid="minute-format-locks">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#14141b] border-2 border-[#5d8a52] px-3 py-1 text-xs font-extrabold text-white"><Lock size={12} className="text-[#8fbf82]" /> Portrait 9:16</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#14141b] border-2 border-[#5d8a52] px-3 py-1 text-xs font-extrabold text-white"><Lock size={12} className="text-[#8fbf82]" /> Up to 60 seconds</span>
        </div>
      </div>
      <p className={isEdit ? "text-sm text-white/60 mb-5" : "text-base font-extrabold text-white mb-5"}>{isEdit ? "Tweak your reel and save — it re-renders in place. Music, voice-over and cover stay as they are unless you change them." : "A 60-second portrait reel — your video clips & photos, on-screen text, music, an optional voice-over and your own cover thumbnail."}</p>

      {activeRender && (
        <div data-testid="minute-render-banner" className="mb-5 flex items-center justify-between gap-3 rounded-xl px-4 py-3 bg-[#2a1416] border-2 border-[#dc2626]/60">
          <div className="flex items-center gap-2 min-w-0">
            <Loader2 size={16} className="spin text-[#ffd21e] shrink-0" />
            <span className="text-sm font-bold text-white truncate">
              {renderState?.status === "cancelling" ? "Cancelling render…" : `Rendering this reel… ${renderState?.progress ?? 0}%`}
            </span>
          </div>
          <button
            data-testid="minute-cancel-render"
            onClick={cancelThisRender}
            disabled={cancelBusy || renderState?.status === "cancelling"}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-[#dc2626] hover:bg-[#b91c1c] disabled:opacity-60 text-white text-xs font-bold px-3 py-2 transition-colors"
          >
            <X size={15} strokeWidth={3} /> Cancel Render
          </button>
        </div>
      )}

      {/* Layout picker */}
      <label className="block text-xs font-extrabold text-white/70 mb-2">Clip mix <span className="text-white/40">(video clips, photos or both — up to 60s)</span></label>
      <div className="space-y-2.5 mb-6" data-testid="minute-layout-picker">
        {Object.entries(LAYOUTS).map(([key, cfg]) => {
          const active = layout === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`minute-layout-${key}`}
              onClick={() => pickLayout(key)}
              className="flex items-center gap-3 w-full text-left group"
            >
              <span
                className="shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all"
                style={active ? { background: "#ffd21e", borderColor: "#ffd21e" } : { background: "transparent", borderColor: "#5d8a52" }}
              >
                {active && <Check size={14} className="text-[#171426]" strokeWidth={3.5} />}
              </span>
              <span className={`text-sm font-semibold transition-colors ${active ? "text-[#ffd21e]" : "text-white/75 group-hover:text-white"}`}>
                {cfg.desc}
              </span>
            </button>
          );
        })}
      </div>

      {/* Title */}
      <label className="block text-xs font-extrabold text-white/70 mb-1">Title</label>
      <input
        data-testid="minute-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. 5 Knife Skills Every Home Cook Needs"
        className="sms-input w-full px-4 py-2.5 text-sm mb-5"
        maxLength={120}
      />

      {/* Slides */}
      {durations.includes(10) && (
        <p className="text-xs text-white/60 mb-2 flex items-start gap-1.5" data-testid="minute-vo-choice-note">
          <span className="text-[#8fbf82]">🎙️</span>
          <span>Record your voice-over on each <b className="text-white">10-second clip</b> — or in our <b className="text-white">Recording Studio</b> below. It's one or the other.</span>
        </p>
      )}
      <div className="space-y-3" data-testid="minute-slides">
        {slides.map((s, i) => {
          const preview = s.image || (s.image_id ? imageUrl(s.image_id) : null);
          const isVid = durations[i] === 10 && s.kind === "video";
          return (
            <div
              key={i}
              className="rounded-2xl p-3 transition-shadow"
              style={PANEL}
              data-testid={`minute-slide-${i}`}
              onDragOver={(e) => { if (dragFrom.current !== null) e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); moveSlide(dragFrom.current, i); dragFrom.current = null; }}
            >
              {(() => {
                const ctlPos = (
                <select
                  data-testid={`minute-textpos-${i}`}
                  value={s.text_pos || "middle"}
                  onChange={(e) => patch(i, { text_pos: e.target.value })}
                  title="Where the headline sits on the video"
                  className="sms-input w-full min-w-0 px-2.5 py-2 text-xs font-bold text-white bg-black cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <option value="top">Text · Top</option>
                  <option value="middle">Text · Middle</option>
                  <option value="bottom">Text · Bottom</option>
                </select>
                );
                const ctlSize = (
                <select
                  data-testid={`minute-textsize-${i}`}
                  value={s.text_size || "medium"}
                  onChange={(e) => patch(i, { text_size: e.target.value })}
                  title="Headline text size"
                  className="sms-input w-full min-w-0 px-2.5 py-2 text-xs font-bold text-white bg-black cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <option value="small">Size · Small</option>
                  <option value="medium">Size · Medium</option>
                  <option value="large">Size · Large</option>
                </select>
                );
                const ctlMove = (
                <select
                  data-testid={`minute-motion-${i}`}
                  disabled={isVid}
                  value={s.motion || "none"}
                  onChange={(e) => patch(i, { motion: e.target.value })}
                  title="Camera movement on this photo (text stays pinned)"
                  className="sms-input w-full min-w-0 px-2.5 py-2 text-xs font-bold text-white bg-black cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <option value="none">Move · None</option>
                  <option value="push_in">Move · Push In</option>
                  <option value="reveal">Move · Reveal</option>
                  <option value="dolly_left">Move · Dolly L</option>
                  <option value="dolly_right">Move · Dolly R</option>
                  <option value="auto">Move · Auto ✨</option>
                </select>
                );
                const ctlMaster = (
                <div className="flex flex-wrap gap-1.5 min-w-0">
                {(() => {
                  const isMaster = masterIdx === i;
                  const consistent = masterIdx !== null;
                  const base = "flex-1 min-w-0 inline-flex items-center justify-center gap-1 rounded-full px-2.5 py-2 text-xs font-bold border-2 transition-all whitespace-nowrap";
                  if (isMaster) {
                    return (
                      <>
                        <button
                          type="button"
                          data-testid={`minute-master-${i}`}
                          onClick={() => toggleMaster(i)}
                          title="This slide's person is the master. Other slides you generate will match this character. Click to turn Consistent Character off."
                          className={base}
                          style={{ background: "#ffd21e", borderColor: "#ffd21e", color: "#171426" }}
                        >
                          <Star size={12} fill="#171426" /> Master character
                        </button>
                        <button
                          type="button"
                          data-testid={`minute-wardrobe-${i}`}
                          onClick={() => { setLockWardrobe((v) => !v); toast.message(lockWardrobe ? "Outfit lock off" : "Outfit locked — same clothing on every slide"); }}
                          title="Also keep the EXACT same outfit (clothing, colours, accessories) on every generated slide — not just the same face."
                          className={base}
                          style={lockWardrobe
                            ? { background: "#ffd21e", borderColor: "#ffd21e", color: "#171426" }
                            : { background: "#000", borderColor: "#5d8a52", color: "#fff" }}
                        >
                          {lockWardrobe ? <Check size={12} /> : <span className="w-3 h-3 rounded-[3px] border border-current inline-block" />} Lock outfit
                        </button>
                      </>
                    );
                  }
                  if (consistent) {
                    return (
                      <button
                        type="button"
                        data-testid={`minute-master-${i}`}
                        onClick={() => toggleMaster(i)}
                        title={`New images here will match the character on Slide ${masterIdx + 1}. Click to make THIS slide the master instead.`}
                        className={base}
                        style={{ background: "rgba(255,210,30,.12)", borderColor: "rgba(255,210,30,.5)", color: "#ffd21e" }}
                      >
                        <Star size={12} /> Matches Slide {masterIdx + 1}
                      </button>
                    );
                  }
                  return (
                    <button
                      type="button"
                      data-testid={`minute-master-${i}`}
                      onClick={() => toggleMaster(i)}
                      title="Consistent Character: make this slide's person the master so every slide you generate shows the SAME person in new poses and scenes."
                      className={base}
                      style={{ background: "#000", borderColor: "#5d8a52", color: "#fff" }}
                    >
                      <span className="w-3 h-3 rounded-[3px] border border-current inline-block" /> Consistent Character
                    </button>
                  );
                })()}
                </div>
                );
                const body = ({ button, meter }) => (
                  <>
                    <div className="flex items-start gap-3">
                    <div className="shrink-0">
                      <button
                        type="button"
                        data-testid={`minute-slide-photo-${i}`}
                        onClick={() => (isVid ? (s.clipFile && s.clipDur > 10.5 && !s.serverPoster ? setTrimIdx(i) : videoRefs.current[i]?.click()) : preview ? openCrop(i) : fileRefs.current[i]?.click())}
                        title={preview ? "Tap to crop to portrait 9:16" : "Upload a picture"}
                        className="w-[120px] h-[214px] rounded-xl border-2 border-dashed border-[#5d8a52] bg-black flex flex-col items-center justify-center overflow-hidden hover:border-[#a9d79b] transition-all relative"
                      >
                        {preview ? (
                          <SlideFilteredImage src={preview} filter={s.filter || "none"} strength={s.filter_strength || "standard"} alt={`slide ${i + 1}`} className="w-full h-full object-cover" />
                        ) : isVid ? (
                          <><Film size={22} className="text-[#f0c975]" /><span className="text-[10px] font-bold text-[#f0c975] mt-1">Add Video</span></>
                        ) : (
                          <><ImagePlus size={20} className="text-[#a9d79b]" /><span className="text-[9px] font-bold text-[#a9d79b] mt-1">Upload</span></>
                        )}
                        {isVid && s.clipFile && (
                          <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-black/75 px-1.5 py-0.5 text-[9px] font-extrabold text-[#f0c975]" data-testid={`minute-video-badge-${i}`}>
                            <Film size={9} /> {s.clipDur ? `${Math.min(10, Math.round(s.clipDur))}s` : "≤10s"}
                          </span>
                        )}
                        {isVid && s.clipFile && s.clipDur > 10.5 && !s.serverPoster && (
                          <span role="button" onClick={(e) => { e.stopPropagation(); setTrimIdx(i); }} data-testid={`minute-trim-${i}`}
                            className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-[#f0c975] px-1.5 py-0.5 text-[9px] font-extrabold text-black hover:bg-white transition-colors">
                            <Scissors size={9} /> Trim
                          </span>
                        )}
                        <span className="absolute top-1 left-1 text-[9px] font-extrabold text-black bg-[#ffd21e] px-1.5 rounded">{durations[i]}s</span>
                        {isVid ? (
                          <span role="button" data-testid={`minute-record-clip-${i}`} onClick={(e) => { e.stopPropagation(); setVidCamIdx(i); }} title="Record a 10-second clip with your camera"
                            className="absolute bottom-1 left-1 w-8 h-8 rounded-full bg-[#dc2626] border-2 border-white flex items-center justify-center text-white shadow-md hover:bg-[#b91c1c] active:scale-95 transition-[background-color,transform]">
                            <Video size={14} />
                          </span>
                        ) : <span
                          role="button"
                          data-testid={`minute-slide-camera-${i}`}
                          onClick={(e) => { e.stopPropagation(); setCamIndex(i); }}
                          title="Take a photo with your camera"
                          className="absolute bottom-1 left-1 w-6 h-6 rounded-full bg-black/70 border border-[#5d8a52] flex items-center justify-center text-[#a9d79b] hover:text-white hover:border-[#a9d79b] transition-colors"
                        >
                          <Camera size={12} />
                        </span>}
                        {preview && !isVid && (
                          <span
                            role="button"
                            data-testid={`minute-slide-replace-${i}`}
                            onClick={(e) => { e.stopPropagation(); fileRefs.current[i]?.click(); }}
                            title="Replace picture"
                            className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-black/70 border border-[#5d8a52] flex items-center justify-center text-[#a9d79b] hover:text-white hover:border-[#a9d79b] transition-colors"
                          >
                            <ImagePlus size={12} />
                          </span>
                        )}
                      </button>
                      <input ref={(el) => (fileRefs.current[i] = el)} data-testid={`minute-slide-file-${i}`} type="file" accept="image/*" onChange={(e) => onUpload(i, e)} className="hidden" />
                      <input ref={(el) => (videoRefs.current[i] = el)} data-testid={`minute-slide-video-file-${i}`} type="file" accept="video/*" onChange={(e) => { onPickVideo(i, e.target.files?.[0]); e.target.value = ""; }} className="hidden" />
                      {durations[i] === 10 && (
                        <div className={`mt-1.5 w-[120px] grid grid-cols-2 rounded-full border-2 overflow-hidden text-[10px] font-extrabold transition-colors ${isVid ? "border-[#f0c975]" : "border-[#5d8a52]"}`} data-testid={`minute-kind-${i}`}>
                          {[["video", "Video", Film], ["image", "Image", ImageIcon]].map(([k, label, Icon]) => (
                            <button key={k} type="button" data-testid={`minute-kind-${k}-${i}`}
                              onClick={() => (s.kind || "image") !== k && patch(i, { kind: k, image: null, image_id: null, clipFile: null, clipDur: 0, keepSound: false, trimStart: 0, origSrc: null, cropped: false })}
                              className={`py-1 inline-flex items-center justify-center gap-0.5 focus:outline-none transition-colors ${(s.kind || "image") === k ? (k === "video" ? "bg-[#f0c975] text-black" : "bg-[#5d8a52] text-white") : "text-white/70 hover:bg-white/10"}`}>
                              <Icon size={10} /> {label}
                            </button>
                          ))}
                        </div>
                      )}
                      {isVid && s.clipFile && (
                        <button type="button" data-testid={`minute-keep-sound-${i}`} onClick={() => patch(i, { keepSound: !s.keepSound })}
                          className={`mt-1.5 w-[120px] inline-flex items-center justify-center gap-1 rounded-full px-1 py-1 text-[10px] font-extrabold border-2 transition-colors ${s.keepSound ? "border-[#f0c975] text-[#f0c975]" : "border-white/20 text-white/60"}`}>
                          {s.keepSound ? <Volume2 size={11} /> : <VolumeX size={11} />} {s.keepSound ? "Sound on" : "Sound off"}
                        </button>
                      )}
                      {preview && !isVid && (
                        <button
                          type="button"
                          data-testid={`minute-slide-crop-${i}`}
                          onClick={() => openCrop(i)}
                          className="mt-1 w-[120px] inline-flex items-center justify-center gap-1 text-[9px] font-extrabold text-[#a9d79b] hover:text-white transition-colors"
                        >
                          <Crop size={10} /> {s.cropped ? "Re-crop" : "Crop 9:16"}
                        </button>
                      )}
                    </div>
                      <div className="flex-1 min-w-0 self-stretch flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                        <span
                          draggable
                          onDragStart={() => { dragFrom.current = i; }}
                          onDragEnd={() => { dragFrom.current = null; }}
                          data-testid={`minute-slide-drag-${i}`}
                          title="Drag to reorder"
                          className="cursor-grab active:cursor-grabbing text-white/30 hover:text-[#a9d79b] transition-colors"
                        >
                          <GripVertical size={16} />
                        </span>
                        <span className="text-[11px] font-extrabold text-white bg-[#1e40af] rounded-full px-2.5 py-0.5 shadow-sm" data-testid={`minute-slide-label-${i}`}>Slide {i + 1}</span>
                        <div className="ml-auto flex items-center gap-1">
                          <button type="button" disabled={i === 0} onClick={() => moveSlide(i, i - 1)} data-testid={`minute-slide-up-${i}`} title="Move up" className="w-6 h-6 rounded-md bg-white/5 hover:bg-white/15 text-white/70 disabled:opacity-25 flex items-center justify-center transition-colors"><ChevronUp size={14} /></button>
                          <button type="button" disabled={i === slides.length - 1} onClick={() => moveSlide(i, i + 1)} data-testid={`minute-slide-down-${i}`} title="Move down" className="w-6 h-6 rounded-md bg-white/5 hover:bg-white/15 text-white/70 disabled:opacity-25 flex items-center justify-center transition-colors"><ChevronDown size={14} /></button>
                        </div>
                        </div>
                        <textarea
                          data-testid={`minute-headline-${i}`}
                          value={s.headline}
                          onChange={(e) => patch(i, { headline: e.target.value })}
                          placeholder="Headline (big on-screen text)"
                          rows={2}
                          className="sms-input w-full px-3 py-2 text-sm font-bold resize-none leading-snug"
                          maxLength={120}
                        />
                        <input
                          data-testid={`minute-subtext-${i}`}
                          value={s.subtext}
                          onChange={(e) => patch(i, { subtext: e.target.value })}
                          placeholder="Sub-line (optional)"
                          className="sms-input w-full px-3 py-2 text-sm"
                          maxLength={240}
                        />
                        {isDesk ? (
                          <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center gap-3 pt-1" data-testid={`minute-clip-controls-row-${i}`}>
                            <div className="flex flex-col gap-2 min-w-0">{ctlMaster}{ctlMove}</div>
                            <div className="flex items-center justify-center min-w-[64px]" data-testid={`minute-clip-record-slot-${i}`}>{button}</div>
                            <div className="flex flex-col gap-2 min-w-0">{ctlPos}{ctlSize}</div>
                          </div>
                        ) : button && <div className="flex-1 flex items-center justify-center pt-1" data-testid={`minute-clip-record-slot-${i}`}>{button}</div>}
                      </div>
                    </div>
                    <div className={`flex gap-2 mt-2 ${isVid ? "hidden" : ""}`}>
                      <button
                        type="button"
                        data-testid={`minute-generate-${i}`}
                        onClick={() => onGenerate(i)}
                        disabled={s.busy}
                        className="w-[120px] shrink-0 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#4f7746] hover:bg-[#5d8a52] disabled:opacity-50 px-2 py-2 text-xs font-extrabold text-white transition-colors"
                      >
                        {s.busy ? <Loader2 size={13} className="spin" /> : <Sparkles size={13} />} Generate
                      </button>
                      <textarea
                        data-testid={`minute-prompt-${i}`}
                        value={s.prompt}
                        onChange={(e) => patch(i, { prompt: e.target.value })}
                        placeholder="Describe an image to generate…"
                        rows={2}
                        className="sms-input flex-1 min-w-0 px-3 py-2 text-xs resize-none leading-snug sm:h-[34px] sm:py-[9px] sm:overflow-hidden"
                        maxLength={200}
                      />
                    </div>
                    {!isDesk && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {ctlPos}{ctlSize}{ctlMove}{ctlMaster}
                    </div>
                    )}
                    <div className="space-y-2 mt-2">
                {preview && (
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid={`minute-filters-${i}`}>
                    <span className="text-[10px] font-extrabold text-white/40 mr-0.5">Look</span>
                    {FILTER_OPTIONS.map((f) => {
                      const on = (s.filter || "none") === f.key;
                      return (
                        <button
                          key={f.key}
                          type="button"
                          data-testid={`minute-filter-${i}-${f.key}`}
                          onClick={() => patch(i, { filter: on ? "none" : f.key })}
                          className="px-2 py-0.5 rounded-full text-[10px] font-extrabold border transition-all"
                          style={on
                            ? { background: "#ffd21e", borderColor: "#ffd21e", color: "#171426" }
                            : { background: "transparent", borderColor: "#3a4a34", color: "#cfe3c6" }}
                        >
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {preview && s.filter && s.filter !== "none" && (
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid={`minute-strength-${i}`}>
                    <span className="text-[10px] font-extrabold text-white/40 mr-0.5">Strength</span>
                    {STRENGTH_OPTIONS.map((st) => {
                      const on = (s.filter_strength || "standard") === st.key;
                      return (
                        <button
                          key={st.key}
                          type="button"
                          data-testid={`minute-strength-${i}-${st.key}`}
                          onClick={() => patch(i, { filter_strength: st.key })}
                          title={`Dial the ${s.filter} look ${st.label.toLowerCase()}`}
                          className="px-2 py-0.5 rounded-full text-[10px] font-extrabold border transition-all"
                          style={on
                            ? { background: "#7fbf82", borderColor: "#7fbf82", color: "#0f1a0c" }
                            : { background: "transparent", borderColor: "#3a4a34", color: "#cfe3c6" }}
                        >
                          {st.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                    </div>
                    {meter && <div className="mt-2 pt-2 border-t border-white/10" data-testid={`minute-clip-vo-${i}`}>{meter}</div>}
                  </>
                );
                return durations[i] === 10 ? (
                  <DiyStepRecorder
                    index={i}
                    idPrefix="minute-clip"
                    compact
                    maxSecs={10}
                    take={s.vo}
                    voice={s.vo ? "mine" : "none"}
                    onTake={(d) => patch(i, d ? { vo: d, keepSound: false } : { vo: d })}
                    onVoice={() => {}}
                    disabled={!!voDataUrl}
                    disabledNote="Using the Recording Studio take — delete it there to record per clip"
                    renderLayout={body}
                  />
                ) : body({ button: null, meter: null });
              })()}
            </div>
          );
        })}
      </div>

      {/* Voice Studio (horizontal) on top, then Music Presets (songs in two columns) below —
          both full-width, stacking nicely above the already-horizontal Music mix card. */}
      <div className="mt-5 space-y-4">
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 font-extrabold text-sm text-white mb-2">🎙️ Voice-over studio <span className="text-[10px] text-white/40 font-bold">(optional · one 60s take)</span></div>
          {hasClipVo && (
            <p className="text-[11px] font-bold text-[#f0c975] mb-2" data-testid="minute-studio-locked-note">You're recording per clip — delete your clip takes to use the studio instead.</p>
          )}
          <div className={hasClipVo ? "opacity-40 pointer-events-none select-none" : ""} aria-disabled={hasClipVo} data-testid="minute-studio-wrap">
          <MinuteVoiceStudio dataUrl={voDataUrl} onRecorded={(url) => setVoDataUrl(url)} onClear={() => setVoDataUrl(null)} defaultScript={voScript} onScriptChange={setVoScript} onBrandPack={handleBrandPack} />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 font-extrabold text-sm text-white mb-2">
            <Music size={15} className="text-[#8fbf82]" /> Music Presets
            <span className="text-[10px] text-white/40 font-bold">(up to {musicMax} songs · {tracks.length} saved)</span>
          </div>
          <div className="rounded-2xl p-3 flex-1 flex flex-col" style={PANEL} data-testid="minute-music">
            <div className="grid md:grid-cols-2 gap-3 flex-1" data-testid="minute-song-slots">
              {Array.from({ length: musicMax }).map((_, i) => {
                const t = tracks[i];
                if (t) {
                  const active = selectedTrackId === t.id;
                  return (
                    <div key={t.id} data-testid={`minute-song-${i}`} onClick={() => setSelectedTrackId(active ? null : t.id)}
                      className="rounded-xl border-2 px-3 py-2 cursor-pointer transition-all"
                      style={active ? { borderColor: "#ffd21e", background: "rgba(255,210,30,.08)" } : { borderColor: "#3a4a34", background: "#000" }}>
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center" style={{ background: active ? "#ffd21e" : "rgba(255,255,255,.08)", color: active ? "#171426" : "#8fbf82" }}>
                          {active ? <Check size={14} /> : <Music size={13} />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold text-white truncate" data-testid={`minute-song-name-${i}`}>{t.name}</div>
                          {t.youtube_url ? (
                            <a href={t.youtube_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[10px] text-[#ff6b81] hover:text-[#ff8ea0] truncate max-w-full" data-testid={`minute-song-yt-${i}`}>
                              <Youtube size={11} className="shrink-0" /> <span className="truncate">{t.youtube_url}</span>
                            </a>
                          ) : (<div className="text-[10px] text-white/35">No reference link</div>)}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); deleteSong(t.id); }} data-testid={`minute-song-delete-${i}`} title="Remove song" className="shrink-0 text-white/40 hover:text-[#f7a0b4] transition-colors p-1"><X size={15} /></button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={`empty-${i}`} className="rounded-xl border-2 border-dashed border-[#3a4a34] px-3 py-2" data-testid={`minute-song-empty-${i}`}>
                    <button type="button" onClick={() => songRefs.current[i]?.click()} disabled={musicBusy} data-testid={`minute-song-upload-${i}`}
                      className="w-full flex items-center gap-2 text-left text-[#a9d79b] hover:text-white disabled:opacity-50 transition-colors">
                      <span className="w-6 h-6 shrink-0 rounded-full bg-white/8 flex items-center justify-center">{musicBusy ? <Loader2 size={13} className="spin" /> : <Plus size={14} />}</span>
                      <span className="text-sm font-bold">Upload MP3</span>
                    </button>
                    <input ref={(el) => (songRefs.current[i] = el)} type="file" accept="audio/mpeg,audio/mp3,.mp3" onChange={(e) => onUploadSong(i, e)} className="hidden" data-testid={`minute-song-file-${i}`} />
                    <input value={ytDraft[i] || ""} onChange={(e) => setYtDraft((d) => ({ ...d, [i]: e.target.value }))} placeholder="YouTube link (optional label)"
                      className="mt-1.5 w-full bg-transparent border-b border-[#2a3a26] focus:border-[#5d8a52] outline-none text-[10px] text-white/70 placeholder:text-white/25 pb-1" data-testid={`minute-song-yt-input-${i}`} />
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-white/40 mt-3">Upload up to {musicMax} MP3s to reuse. Tap a song to set it as this reel's track. The YouTube link is just your reference.</p>
            <p className="text-[11px] font-semibold text-[#a9d79b] mt-1.5" data-testid="minute-music-youtube-tip">
              🎵 Need a track? Grab a free one from the{" "}
              <a href="https://studio.youtube.com/channel/UC/music" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">YouTube Audio Library</a>
              {" "}— already cleared for YouTube.
            </p>
          </div>
        </div>
      </div>

      {/* Music mix — how music sits under a voice-over + swell-back-up ending */}
      <div className="mt-4 rounded-2xl p-3" style={PANEL} data-testid="minute-music-mix">
        <div className="grid md:grid-cols-2 gap-4 mb-2">
          <div className="flex items-center gap-1.5 font-extrabold text-sm text-white">
            <Music size={14} className="text-[#8fbf82]" /> Music mix <span className="text-[10px] text-white/40 font-bold">(applies when you add a voice-over)</span>
          </div>
          <div className="flex items-center gap-1.5 font-extrabold text-sm text-white">
            Camera Movement <span className="text-[10px] text-white/40 font-bold">Set camera movement speed</span>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 items-center">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-extrabold text-white/60">Music under your voice</label>
              <span className="text-[11px] font-extrabold text-[#8fbf82]" data-testid="minute-duck-value">{Math.round(musicDuck * 100)}%</span>
            </div>
            <input
              type="range"
              data-testid="minute-duck-slider"
              min="5" max="70" step="1"
              value={Math.round(musicDuck * 100)}
              onChange={(e) => setMusicDuck(Number(e.target.value) / 100)}
              className="w-full accent-[#ffd21e] cursor-pointer"
            />
            <div className="flex justify-between text-[9px] font-bold text-white/35 mt-0.5"><span>Quiet</span><span>Loud</span></div>
          </div>
          <div className="rounded-xl bg-black/40 px-3 py-2.5" data-testid="minute-motion-intensity-row">
            <div className="grid grid-cols-3 gap-1.5">
              {[["subtle", "Subtle"], ["medium", "Medium"], ["strong", "Strong"]].map(([v, l]) => {
                const on = motionIntensity === v;
                return (
                  <button key={v} type="button" data-testid={`minute-intensity-${v}`} onClick={() => setMotionIntensity(v)}
                    className="px-2 py-1.5 rounded-lg text-[11px] font-extrabold border transition-all"
                    style={on ? { background: "#1e3aaa", borderColor: "#5b8cff", color: "#fff" } : { background: "transparent", borderColor: "#3a4a34", color: "#cfe3c6" }}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-xl bg-black/40 px-3 py-2.5" data-testid="minute-quality-row">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-extrabold text-white">Video quality</span>
              <span className="text-[10px] font-bold text-white/40">{quality === "fast" ? "renders quicker" : "sharpest text"}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {[["hd", "HD 1080p", "Crisp"], ["fast", "Fast 720p", "Quicker"]].map(([v, l, tag]) => {
                const on = quality === v;
                return (
                  <button key={v} type="button" data-testid={`minute-quality-${v}`} onClick={() => setQuality(v)}
                    className="px-2 py-1.5 rounded-lg text-[11px] font-extrabold border transition-all flex flex-col items-center leading-tight"
                    style={on ? { background: "#1e3aaa", borderColor: "#5b8cff", color: "#fff" } : { background: "transparent", borderColor: "#3a4a34", color: "#cfe3c6" }}>
                    <span>{l}</span>
                    <span className="text-[9px] font-bold opacity-70">{tag}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl bg-black/40 px-3 py-2.5" data-testid="minute-swell-row">
            <input
              type="checkbox"
              data-testid="minute-swell-toggle"
              checked={musicSwell}
              onChange={(e) => setMusicSwell(e.target.checked)}
              className="w-5 h-5 accent-[#ffd21e] shrink-0 mt-0.5"
            />
            <span className="text-xs font-extrabold text-white leading-snug">Swell the music back up after my voice-over<span className="block text-white/45 font-bold">For a stronger, fuller ending once you stop talking</span></span>
          </label>
        </div>
      </div>

      {/* Thumbnail Creator — its own step, portrait 9:16 */}
      <div className="mt-5" data-testid="minute-thumb-step">
        <div className="flex items-center gap-1.5 font-extrabold text-sm text-white mb-2"><ImageIcon size={15} className="text-[#8fbf82]" /> Cover thumbnail <span className="text-[10px] text-white/40 font-bold">(optional)</span></div>
        <div className="rounded-2xl p-3" style={PANEL}>
          {!thumbOpen ? (
            <button type="button" data-testid="minute-thumb-open" onClick={openThumb} disabled={thumbResolving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#1e3aaa] hover:brightness-110 text-white font-extrabold py-3 disabled:opacity-60 transition-all">
              {thumbResolving ? <><Loader2 size={16} className="spin" /> Preparing your photos…</> : <><Sparkles size={16} /> {thumbId ? "Edit Cover Thumbnail" : "Design a Cover Thumbnail"}</>}
            </button>
          ) : (
            <ThumbnailComposer dark portrait photos={thumbPhotos} defaultTitle={title}
              onUse={(imageId) => { setThumbId(imageId); setThumbOpen(false); toast.success("Cover thumbnail set ✓"); }} />
          )}
          {thumbId && !thumbOpen && (
            <div className="mt-3 flex items-center gap-2">
              <img src={imageUrl(thumbId)} alt="cover" className="w-16 rounded-lg border border-[#5d8a52]" style={{ aspectRatio: "9/16", objectFit: "cover" }} data-testid="minute-thumb-preview" />
              <span className="text-xs text-[#a9d79b] font-bold flex items-center gap-1"><Check size={13} /> Cover ready</span>
            </div>
          )}
        </div>
      </div>

      <label className="mt-5 flex items-center gap-2.5 cursor-pointer select-none rounded-2xl p-3" style={PANEL} data-testid="minute-hide-counter-row">
        <input
          type="checkbox"
          data-testid="minute-hide-counter"
          checked={hideCounter}
          onChange={(e) => setHideCounter(e.target.checked)}
          className="w-5 h-5 accent-[#ffd21e] shrink-0"
        />
        <span className="text-sm font-extrabold text-white">Remove step counter <span className="text-white/50 font-bold">(e.g. “3 of 12”)</span></span>
      </label>

      <MinuteEndcardEditor value={endcard} onChange={setEndcard} />

      {creating ? (
        <div className="mt-3" data-testid="minute-create-progress">
          <div className="w-full relative overflow-hidden rounded-full bg-[#dc2626] shadow-[0_10px_24px_-10px_rgba(220,38,38,.9)]">
            <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500 ease-out" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#ffe066,#ffd21e)" }} data-testid="minute-create-bar" />
            <div className="relative flex items-center justify-center gap-2 py-3.5 font-extrabold text-black">
              <Loader2 size={18} className="spin" />
              <span>{pct < 100 ? (isEdit ? "Saving your reel…" : "Building your reel…") : "Done!"}</span>
              <span className="tabular-nums" data-testid="minute-create-pct">{Math.round(pct)}%</span>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-center gap-4 text-xs font-bold">
            <span className="inline-flex items-center gap-1.5 text-white tabular-nums" data-testid="minute-render-stopwatch">
              <Clock size={13} className="text-[#ffd21e]" /> {fmtTime(elapsed)}
            </span>
          </div>
          <p className="text-center text-[11px] text-white/45 mt-1 tabular-nums leading-snug" data-testid="minute-render-avg">
            Typical render ~4:00 mins <span className="text-white/30">(no movement)</span> · ~9:00 mins <span className="text-white/30">(with movement)</span>
          </p>
          {pct >= 95 && pct < 100 && (
            <p className="text-center text-sm font-extrabold text-[#ffd21e] mt-2.5" data-testid="minute-almost-done-note">
              Your video is almost done… please wait 🎬
            </p>
          )}
          <p className="text-center text-[11px] text-white/50 mt-2" data-testid="minute-leave-note">
            You may leave this page while your video is rendering — it keeps going on our servers and lands on your Dashboard when done.
          </p>
        </div>
      ) : renderReady ? (
        <div className="mt-3 rounded-2xl border-2 p-4 text-center" style={{ borderColor: "#5d8a52", background: "#0f0f16" }} data-testid="minute-render-done">
          <div className="inline-flex items-center gap-2 font-extrabold text-white text-base"><CheckCircle2 size={20} className="text-[#8fbf82]" /> Your reel is ready! 🎬</div>
          <p className="text-xs text-white/55 mt-1 tabular-nums">Rendered in {fmtTime(elapsed)}{avgSeconds > 0 ? ` · typical ~${fmtTime(avgSeconds)}` : ""}</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button data-testid="minute-view-dashboard-btn" onClick={() => navigate("/my-videos")} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3aaa] hover:brightness-110 text-white font-extrabold py-3 transition-all">
              <Play size={17} /> View in Dashboard
            </button>
            <button data-testid="minute-make-another-btn" onClick={() => { setRenderReady(null); setPct(0); }} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-transparent border-2 border-[#5d8a52] hover:bg-[#5d8a52]/15 text-white font-extrabold py-3 transition-colors">
              Keep editing
            </button>
          </div>
          {/* Post straight to social — this reel is 9:16, perfect for Facebook & Instagram */}
          <div className="mt-4 pt-4 border-t border-[#5d8a52]/40" data-testid="minute-share-social">
            <p className="text-[11px] font-extrabold tracking-wide text-white/50 uppercase mb-2">Share your reel</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                data-testid="minute-share-facebook"
                onClick={shareToFacebook}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full text-white font-extrabold py-2.5 transition-transform hover:-translate-y-0.5"
                style={{ background: "#1877F2" }}
              >
                <Facebook size={17} /> Share to Facebook
              </button>
              <button
                data-testid="minute-share-instagram"
                onClick={saveForInstagram}
                disabled={sharingIg}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full text-white font-extrabold py-2.5 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: "linear-gradient(90deg,#F58529,#DD2A7B,#8134AF)" }}
              >
                {sharingIg ? <Loader2 size={17} className="animate-spin" /> : <Instagram size={17} />} Save for Instagram
              </button>
            </div>
            {!captionOpen ? (
              <button
                data-testid="minute-copy-caption"
                onClick={() => loadCaption(false)}
                disabled={captionBusy}
                className="w-full mt-2 inline-flex flex-col items-center justify-center gap-0.5 rounded-full border-2 border-[#5d8a52] bg-[#5d8a52]/10 hover:bg-[#5d8a52]/20 text-white font-extrabold py-2.5 transition-colors disabled:opacity-60"
              >
                <span className="inline-flex items-center gap-2">
                  {captionBusy ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />} Write Social Caption
                </span>
                <span className="text-[10px] font-semibold text-white/55">A ready-to-post caption + relevant hashtags — tweak before you copy</span>
              </button>
            ) : (
              <div className="mt-2 rounded-xl border border-[#5d8a52]/60 bg-black/30 p-2.5" data-testid="minute-caption-editor">
                <textarea
                  data-testid="minute-caption-text"
                  value={captionText}
                  onChange={(e) => setCaptionText(e.target.value)}
                  rows={5}
                  className="w-full resize-y rounded-lg bg-[#0f0f16] border border-[#5d8a52]/50 text-white text-sm leading-snug p-2.5 focus:outline-none focus:border-[#8fbf82]"
                  placeholder="Your caption + hashtags…"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    data-testid="minute-caption-regenerate"
                    onClick={() => loadCaption(true)}
                    disabled={captionBusy}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-[#5d8a52] hover:bg-[#5d8a52]/15 text-white font-extrabold py-2 text-sm transition-colors disabled:opacity-60"
                  >
                    {captionBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Regenerate
                  </button>
                  <button
                    data-testid="minute-caption-copy"
                    onClick={copyCaptionText}
                    disabled={!captionText.trim()}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#4f7746] hover:bg-[#3f6238] text-white font-extrabold py-2 text-sm transition-colors disabled:opacity-60"
                  >
                    <Copy size={15} /> Copy
                  </button>
                </div>
              </div>
            )}
            <p className="text-[11px] text-white/45 mt-2 leading-snug">
              Facebook opens a share window for your public reel link. Instagram has no web posting, so we save the reel — open Instagram on your phone and post it as a Reel. <span className="inline-flex items-center gap-1 text-white/60"><Download size={11} /> same as downloading.</span>
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <button
            data-testid="minute-preview-btn"
            onClick={openPreview}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-transparent border-2 border-[#5d8a52] hover:bg-[#5d8a52]/15 text-white font-extrabold py-3 transition-colors"
          >
            <Play size={17} /> Preview Reel
          </button>
          <button
            data-testid="minute-create-btn"
            onClick={createMinute}
            className="w-full inline-flex flex-col items-center justify-center gap-0.5 rounded-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-extrabold py-3 transition-colors shadow-[0_10px_24px_-10px_rgba(220,38,38,.9)]"
          >
            <span className="inline-flex items-center gap-2"><Zap size={18} /> {isEdit ? "Save & Re-render" : "Create My Minute"}{!isEdit && !isAdmin ? " · $1.99" : ""}</span>
            <span className="text-[11px] font-bold opacity-90 inline-flex items-center gap-1" data-testid="minute-create-balance">
              {isEdit ? "Edits & re-renders are free" : isAdmin ? "Unlimited · no charge" : <>Your balance: ${balanceUsd.toFixed(2)}{lowBalance ? " — tap to add funds" : ""}</>}
            </span>
          </button>
        </div>
      )}
      <p className="text-center text-[11px] text-white/40 mt-2">{total}s · {filledCount} {filledCount === 1 ? "clip" : "clips"} · Portrait 9:16{!isEdit && !isAdmin ? " · $1.99 charged once from your balance" : isEdit ? " · Edits & re-renders are free" : ""}</p>

      {showPreview && (
        <MinutePreviewModal slides={filledEntries.map((x) => x.s)} durations={filledEntries.map((x) => x.dur)} hideCounter={hideCounter} trackId={selectedTrackId} voUrl={previewVo} endcard={endcard} onClose={() => setShowPreview(false)} />
      )}

      <VideoTrimModal
          file={trimIdx != null ? slides[trimIdx]?.clipFile : null}
          duration={trimIdx != null ? slides[trimIdx]?.clipDur : 0}
          initialStart={trimIdx != null ? slides[trimIdx]?.trimStart || 0 : 0}
          onClose={() => setTrimIdx(null)}
          onConfirm={(start, poster) => { patch(trimIdx, { trimStart: start, ...(poster ? { image: poster } : {}) }); setTrimIdx(null); }}
        />
      <CameraClipModal open={vidCamIdx != null} onClose={() => setVidCamIdx(null)} onUse={(file, secs) => onPickVideo(vidCamIdx, file, secs)} />
      {camIndex !== null && (
        <CameraCaptureModal
          portrait
          onCapture={(dataUrl) => { const i = camIndex; patch(i, { image: dataUrl, image_id: null, origSrc: dataUrl, cropped: false }); setCropIndex(i); setCropSrc(dataUrl); }}
          onClose={() => setCamIndex(null)}
        />
      )}

      {cropSrc && cropIndex !== null && (
        <PortraitCropModal
          image={cropSrc}
          onCancel={closeCrop}
          onConfirm={(dataUrl) => {
            patch(cropIndex, { image: dataUrl, image_id: null, cropped: true });
            closeCrop();
            toast.success("Portrait crop set ✂️");
          }}
        />
      )}

      {pickerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" data-testid="minute-sitephoto-picker" onClick={() => setPickerOpen(false)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-auto rounded-2xl border-2 border-[#5d8a52] bg-[#13131a] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <h3 className="font-extrabold text-white text-lg flex items-center gap-2"><ImageIcon size={18} className="text-[#8fbf82]" /> Use photos from your site</h3>
                <p className="text-xs text-white/50 mt-0.5">Tap the photos you want. They’ll fill your empty picture slots.</p>
              </div>
              <button onClick={() => setPickerOpen(false)} data-testid="minute-sitephoto-close" className="shrink-0 text-white/50 hover:text-white p-1"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
              {brandPhotos.map((p) => {
                const on = !!pickedPhotos[p.image_id];
                return (
                  <button
                    key={p.image_id}
                    type="button"
                    data-testid={`minute-sitephoto-${p.image_id}`}
                    onClick={() => setPickedPhotos((m) => ({ ...m, [p.image_id]: !m[p.image_id] }))}
                    className="relative rounded-xl overflow-hidden border-2 transition-all aspect-square"
                    style={{ borderColor: on ? "#ffd21e" : "#3a4a34" }}
                  >
                    <img src={imageUrl(p.image_id)} alt="site" className="w-full h-full object-cover" />
                    {on && (
                      <span className="absolute inset-0 bg-[#ffd21e]/25 flex items-center justify-center">
                        <span className="w-7 h-7 rounded-full bg-[#ffd21e] text-black flex items-center justify-center"><Check size={16} /></span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-3 mt-4">
              <span className="text-xs font-bold text-white/50">{Object.values(pickedPhotos).filter(Boolean).length} selected</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPickerOpen(false)} data-testid="minute-sitephoto-skip" className="rounded-full px-4 py-2 text-sm font-bold text-white/70 hover:text-white border border-white/15">Skip</button>
                <button onClick={applyPickedPhotos} data-testid="minute-sitephoto-add" className="rounded-full px-5 py-2 text-sm font-extrabold text-black bg-[#ffd21e] hover:bg-[#f5c800] transition-colors">Add selected</button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
